/**
 * T-053: Pocket telemetry card.
 *
 * Top-10 User-Agent strings from unit_checkins (last 7 days) plus
 * aggregate totals. Sentry and Pocket-ingest toggles render greyed
 * placeholders until the upstream tickets land.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getTelemetry } from '@/lib/pocketSettingsApi';
import type { PocketTelemetry } from '@/types/pocketSettings';

export function TelemetryCard() {
  const [data, setData] = useState<PocketTelemetry | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTelemetry()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        // Non-fatal.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          Pocket telemetry
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          User-Agent strings captured during check-ins. Useful for spotting
          stale Pocket builds in the field. Sentry + Pocket ingest toggles
          land in follow-up tickets.
        </p>

        {!data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Tile
                label={`Check-ins (${data.window_days}d)`}
                value={String(data.total_checkins)}
              />
              <Tile
                label="Distinct versions"
                value={String(data.distinct_user_agents)}
              />
            </div>

            {data.top_user_agents.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No check-in telemetry in the last {data.window_days} days.
              </p>
            ) : (
              <div className="rounded-md border border-border/60 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User-Agent</TableHead>
                      <TableHead className="w-20 text-right">Count</TableHead>
                      <TableHead className="w-20 text-right">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.top_user_agents.map((row) => (
                      <TableRow key={row.user_agent}>
                        <TableCell className="font-mono text-xs max-w-md truncate">
                          {row.user_agent}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.count}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {row.share_pct}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="mt-6 rounded-md border border-dashed border-border/60 p-4 bg-muted/20">
              <div className="text-xs font-medium mb-2 text-muted-foreground">
                Pending upstream tickets
              </div>
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>Sentry server-side capture</span>
                  <span className="text-muted-foreground/50">—</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Pocket ingest endpoint</span>
                  <span className="text-muted-foreground/50">—</span>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">
                {data.deferred_features.note ||
                  'Lights up once upstream tickets land.'}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/50 p-4 bg-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}
